import Header from "@/components/layouts/Header";
import ReportHeader from "@/components/ReportHeader";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ReportButton } from "@/components/ui/report-button";
import VehicleSelect from "@/components/ui/vehicle-select";
import { TrackerPosition } from "@/interfaces";
import { errorHandler, formatDateTime, toggleVisibility } from "@/lib/utils";
import {
  trackReportsService,
  VehicleInOutMapping,
} from "@/services/api/trackReports";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import TravelPositionsDialog from "../TravelReport/TravelPositionsDialog";
import { applyFilters, PositionsReportTableFilters } from "./filters";
import PositionsReportFilters from "./PositionsReportFilters";
import PositionsReportTable from "./PositionsReportTable";

const formSchema = z.object({
  vehicle_id: z
    .number({
      message: "Veículo é obrigatório",
    })
    .min(0, "Veículo é obrigatório"),
  start_hour: z.string(),
  end_hour: z.string(),
  start_date: z
    .string({
      message: "Data é obrigatória",
    })
    .date("Data inválida"),
  end_date: z
    .string({
      message: "Data é obrigatória",
    })
    .date("Data inválida"),
});

type FormValues = z.infer<typeof formSchema>;

const PositionsReport = () => {
  const [selectedPositionData, setSelectedPositionData] = useState<
    TrackerPosition[]
  >([]);
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [date, setDate] = useState("");
  const [startHour, setStartHour] = useState("");
  const [endHour, setEndHour] = useState("");
  const [reportData, setReportData] = useState({
    positions: [] as TrackerPosition[],
    in_out_mapping: [] as VehicleInOutMapping[],
    vehicle_title: "",
  });
  const [dayData, setDayData] = useState<{
    first_movement?: string | null;
    last_movement?: string | null;
    totals?: {
      distance?: number;
    };
    drivers_list?: string[];
  } | null>(null);
  const [filters, setFilters] = useState<PositionsReportTableFilters>({
    ignitionStatus: "all",
    speedRange: "all",
  });

  const filteredPositions = useMemo(() => {
    return applyFilters(reportData.positions, filters);
  }, [reportData.positions, filters]);

  const handleFilterChange = (newFilters: PositionsReportTableFilters) => {
    setFilters(newFilters);
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vehicle_id: undefined,
      start_hour: "00:00",
      end_hour: "23:59",
      start_date: format(new Date(), "yyyy-MM-dd"),
      end_date: format(new Date(), "yyyy-MM-dd"),
    },
  });

  const onSubmit = async (data: FormValues) => {
    setStartHour(data.start_hour);
    setEndHour(data.end_hour);
    setDate(data.start_date);

    data.end_date = data.start_date;

    try {
      const response = await trackReportsService.getPositionsReport(data);

      let positions = [] as TrackerPosition[];
      Object.keys(response.data.positions).forEach((key) => {
        positions = [...positions, ...response.data.positions[key].positions];
      });

      const dateKey = Object.keys(response.data.positions)[0];
      const dayInfo = response.data.positions[dateKey];

      setReportData({
        positions: positions,
        in_out_mapping: response.data.in_out_mapping,
        vehicle_title: response.data.vehicle_title,
      });

      setDayData({
        first_movement: dayInfo?.first_movement,
        last_movement: dayInfo?.last_movement,
        totals: {
          distance: dayInfo?.totals?.distance,
        },
        drivers_list: dayInfo?.drivers_list,
      });

      setIsFormVisible(false);
    } catch (error) {
      errorHandler(error, "Erro ao buscar posições.");
      console.log("🚀 ~ onSubmit ~ error:", error);
    }
  };

  const handleToggleFormVisibility = () => {
    toggleVisibility(isFormVisible, setIsFormVisible);
  };

  const formatTimeInput = (
    value: string,
    onChange: (value: string) => void,
  ) => {
    let cleanValue = value.replace(/\D/g, "");

    if (cleanValue.length > 4) {
      cleanValue = cleanValue.substring(0, 4);
    }

    if (cleanValue.length >= 3) {
      const hours = parseInt(cleanValue.substring(0, 2), 10);
      if (hours > 23) {
        cleanValue = "23" + cleanValue.substring(2);
      }

      if (cleanValue.length >= 4) {
        const minutes = parseInt(cleanValue.substring(2, 4), 10);
        if (minutes > 59) {
          cleanValue = cleanValue.substring(0, 2) + "59";
        }
      }

      cleanValue = cleanValue.substring(0, 2) + ":" + cleanValue.substring(2);
    }

    onChange(cleanValue);
  };

  const handleMapButtonClick = (position: TrackerPosition) => {
    setSelectedPositionData([position]);
    setDialogOpen(true);
  };
  const handleMapDayButtonClick = () => {
    if (reportData.positions.length > 0) {
      setSelectedPositionData(reportData.positions);
      setDialogOpen(true);
    }
  };

  const handleExportPdf = async () => {
    setPdfLoading(true);
    try {
      const params = {
        vehicle_id: form.getValues("vehicle_id"),
        start_date: date,
        end_date: date,
        start_hour: startHour,
        end_hour: endHour,
      };

      const response = await trackReportsService.getPositionsReportPdf(params);

      const pdfBlob = new Blob([response], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl);
    } catch (error) {
      errorHandler(error, "Erro ao gerar PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="h-full">
      <Header
        title="Posições"
        showToggleButton
        isFormVisible={isFormVisible}
        onToggleFormVisibility={handleToggleFormVisibility}
      />

      <div
        className={`max-w-md transition-all duration-300 ease-in-out ${
          isFormVisible ? "opacity-100" : "opacity-0 max-h-0 overflow-hidden"
        }`}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="vehicle_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Veículo</FormLabel>
                  <VehicleSelect
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <DateField form={form} name="start_date" label="Data" />

            <FormField
              control={form.control}
              name="start_hour"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Horário inicial</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      className="w-full"
                      placeholder="__:__"
                      maxLength={5}
                      value={field.value}
                      onChange={(e) =>
                        formatTimeInput(e.target.value, field.onChange)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="end_hour"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Horário final</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      className="w-full"
                      placeholder="__:__"
                      maxLength={5}
                      value={field.value}
                      onChange={(e) =>
                        formatTimeInput(e.target.value, field.onChange)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <ReportButton loading={form.formState.isSubmitting} />
          </form>
        </Form>
      </div>

      {!isFormVisible && (
        <div
          className={`w-full flex flex-col gap-4 transition-all duration-300 ease-in-out ${
            isFormVisible ? "opacity-0 max-h-0 overflow-hidden" : "opacity-100"
          }`}
        >
          {/* Cabecalho do relatorio */}
          <ReportHeader
            handleExportPdf={handleExportPdf}
            primaryInfo={reportData.vehicle_title}
            secondaryInfo={`${date?.split("-").reverse().join("/")} • ${startHour} até ${endHour}`}
            loading={pdfLoading}
          />

          <PositionsReportFilters
            onFilterChange={handleFilterChange}
            onMapButtonClick={handleMapDayButtonClick}
            dayData={dayData}
          />

          {/* Tabela com dados do relatorio */}
          <PositionsReportTable
            positions={filteredPositions}
            handleMapButtonClick={handleMapButtonClick}
            inOutMapping={reportData.in_out_mapping}
          />
        </div>
      )}

      <TravelPositionsDialog
        period={formatDateTime(
          selectedPositionData.length > 0
            ? (selectedPositionData[0]?.date ?? "")
            : "",
        )}
        vehicleTitle={reportData.vehicle_title}
        positions={selectedPositionData}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
};

export default PositionsReport;
